import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import {
  AddPaymentMethods,
  PaymentActionType,
} from "components/sliderPage/manageWallet/addPaymentMethods/AddPaymentMethods";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";
import { SidePageWrapper } from "../../../layout/sidePageWrapper/SidePageWrapper";
import {
  QuickPayBar,
  QuickPayStep,
} from "components/pages/quickPay/QuickPayBar";
import { TranslationAnchor } from "components/common";
import { Expension } from "../expension/Expension";
import { ACH, EWALLET, paymentAmountType, CARD } from "constants/common";
import { AccountMatrix } from "store/account/account-state";
import {
  hasAccountPermission,
  hasClientAccountPermission,
} from "utility/account";

import { useAddNotification, UseInput, useInput, useRequest } from "hooks";
import { useRootDispatch, useRootSelector } from "store";
import { resetInfo, setPaymentData } from "store/payments/payments-slice";
import { PaymentData } from "store/payments/payments-state";
import {
  PaymentMethod,
  PaymentMethodAuthRequest,
  PaymentMethodAuthResponse,
} from "store/paymentMethods/paymentMethods-state";
import {
  activeAccountTypeConfig,
  getAccountRestrictions,
  getPaymentMethods,
  selectedAccountInfo,
} from "store/account/account-selector";

import {
  determineFeeConfiguration,
  calculateFee,
  checkLoanInRange,
  extractError,
  fetchFromConfig,
  getDefaultInputValue,
  renderConditionalProp,
  PAYMENT_METHOD_TYPE,
} from "utility";
import { genericRegex, optionEmailRegex, phoneRegex } from "utility/regex";

import classes from "./Payment.module.scss";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import { clearAllNotifications } from "store/notification/notification-slice";
import { FT_PAYMENT_METHOD_AUTH } from "constants/request";
import { preparePaymentMethodAuthRequest } from "utility/paymentMethod";
import { QuickPayType } from "store/config/payment-center-configuration";
import {
  FT_TOKEN_KEY,
  SESSION_EXPIRED_KEY,
  SESSION_EXPIRED_REASON_INACTIVE,
} from "constants/session";

const isInactive = () =>
  sessionStorage.getItem(SESSION_EXPIRED_KEY) ===
  SESSION_EXPIRED_REASON_INACTIVE;

export const Payment = () => {
  const { t } = useTranslation([
    "quickpay",
    "payment_final",
    "account_next",
    "payment_next",
    "common",
    "payment",
  ]);

  const { request } = useRequest();
  const addNotification = useAddNotification();
  const { paymentCenter, client } = useRootSelector((state) => state.config);
  const { currentPaymentMethod } = useRootSelector((state) => state.common);

  const currentPaymentConfig =
    useRootSelector(getPaymentMethods)[currentPaymentMethod || ACH];
  const paymentData = useRootSelector((state) => state.payments.paymentData);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const config = useRootSelector(activeAccountTypeConfig);
  const { accountMethods } = useRootSelector(getAccountRestrictions);

  const [isLoading, setIsLoading] = useState(false);

  const paymentMethods = useRootSelector(getPaymentMethods);

  const allTabs = Object.keys(paymentMethods);

  const dispatch = useRootDispatch();
  const navigate = useNavigate();

  const isNonValidated =
    paymentCenter.quickPayments.quickPayType === QuickPayType.NonValidated;

  const customerDetails = paymentCenter.quickPayments.customerDetails;
  const showCellPhone = customerDetails?.cellPhone ?? true;
  const showCompanyName = customerDetails?.companyName ?? true;
  const showNote = customerDetails?.note ?? true;

  const commonMatrixMap: Record<string, AccountMatrix> = {
    [ACH]: AccountMatrix.EnableACH,
    [CARD]: AccountMatrix.EnableCard,
    [EWALLET]: AccountMatrix.EnableEWallet,
  };
  Object.keys(commonMatrixMap).forEach((key) => {
    if (!allTabs.includes(key)) {
      delete commonMatrixMap[key];
    }
  });

  const checkAccountRestriction = (
    clientAttribute: number | null | undefined,
    matrixMap: Record<string, AccountMatrix>,
  ) => {
    const hasACH = hasAccountPermission(
      clientAttribute,
      AccountMatrix.EnableACH,
    );
    const hasCard = hasAccountPermission(
      clientAttribute,
      AccountMatrix.EnableCard,
    );
    let restrictionKey = "";

    if (matrixMap[ACH] != null && matrixMap[CARD] != null) {
      if (!hasACH && !hasCard) {
        //Client has ACH and CARD but Account doesnt
        restrictionKey = "quickpay:account-matrix_no-ach-no-card";
      } else if (!hasACH) {
        //Client has ACH and CARD but Account doesnt have ACH
        restrictionKey = "quickpay:account-matrix_no-ach";
      } else if (!hasCard) {
        //Client has ACH and CARD but Account doesnt have Card
        restrictionKey = "quickpay:account-matrix_no-card";
      }
    } else if (matrixMap[ACH] == null && matrixMap[CARD] != null) {
      if (!hasCard) {
        //Client has CARD but Account doesnt
        restrictionKey = "quickpay:account-matrix_no-card";
      }
    } else if (matrixMap[ACH] !== null && matrixMap[CARD] == null) {
      if (!hasACH) {
        //Client has ACH but Account doesnt
        restrictionKey = "quickpay:account-matrix_no-ach";
      }
    }
    return restrictionKey;
  };

  useEffect(() => {
    if (isInactive()) {
      return;
    }
    if (!accountInfo) {
      navigate(`/${client.customerKey}/quick-info`);
    } else {
      if (
        !hasClientAccountPermission(
          accountInfo?.clientAttributes,
          allTabs,
          commonMatrixMap,
        )
      ) {
        //We have some restriction
        const restrictionKey = checkAccountRestriction(
          accountInfo?.clientAttributes,
          commonMatrixMap,
        );
        if (restrictionKey !== "") {
          addNotification({
            translatedMessages: [
              t(`${restrictionKey}`, {
                clientPhone: client.primaryContactPhone,
              }),
            ],
            type: "DEFAULT",
            closable: false,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo]);

  useEffect(() => {
    if (!accountInfo) return;
    if (isInactive()) {
      return;
    }
    const ftToken = sessionStorage.getItem(FT_TOKEN_KEY);
    if (!ftToken) {
      dispatch(resetInfo());
      dispatch(clearAllNotifications());
      navigate(`/${client.customerKey}/quick-info`, { replace: true });
    }
  }, [accountInfo, navigate, client.customerKey, dispatch]);

  const [buttonClick, setbuttonClick] = useState<MakePaymentClick>({
    count: 0,
    scheduleFlag: false,
  });

  const updateButtonClickFromChild = useCallback(
    (updateFn: (prev: MakePaymentClick) => MakePaymentClick) => {
      setbuttonClick((prev) => {
        const newValue = updateFn(prev);
        return newValue;
      });
    },
    [],
  );

  const amountDueInRange = checkLoanInRange(
    accountInfo?.amountDue,
    currentPaymentConfig,
  );
  const otherAmountDueInRange =
    !!accountInfo?.otherAmountDue &&
    checkLoanInRange(accountInfo?.otherAmountDue, currentPaymentConfig);

  const firstName: any = useInput(getDefaultInputValue(paymentData?.firstName));
  const lastName: UseInput = useInput(
    getDefaultInputValue(paymentData?.lastName),
  );
  const email: UseInput = useInput(
    getDefaultInputValue(paymentData?.email, null),
  );
  const phone: UseInput = useInput(getDefaultInputValue(paymentData?.phone));
  const company: UseInput = useInput(
    getDefaultInputValue(paymentData?.company),
  );
  const note: UseInput = useInput(getDefaultInputValue(paymentData?.note));

  const paymentType: UseInput = useInput(
    getDefaultInputValue(fetchFromConfig(config, "initialPaymentTypes")),
  );

  const amountType: UseInput = useInput(
    getDefaultInputValue(
      (() => {
        if (!paymentData?.paymentAmountType) {
          if (amountDueInRange) {
            return paymentAmountType.due_amount;
          } else if (otherAmountDueInRange) {
            return paymentAmountType.other_due_amount;
          } else {
            return paymentAmountType.other;
          }
        } else {
          return paymentData.paymentAmountType;
        }
      })(),
    ),
  );

  const otherAmount: UseInput = useInput(
    getDefaultInputValue(paymentData?.paymentAmount),
  );

  /**
   * This is used to calculate the payment to be made
   * and the mode of payment is bank account
   */
  const validateForm = () => {
    let formIsValid = false;

    const isPhoneValid = !showCellPhone || !!phone.ref.current?.checkValidity();
    const isCompanyValid =
      !showCompanyName || !!company.ref.current?.checkValidity();
    const isNoteValid = !showNote || !!note.ref.current?.checkValidity();

    if (
      firstName.ref.current.checkValidity() &&
      lastName.ref.current.checkValidity() &&
      isCompanyValid &&
      isPhoneValid &&
      email.ref.current.checkValidity() &&
      isNoteValid
    ) {
      formIsValid = true;
    } else {
      firstName.ref.current.reportValidity();
      lastName.ref.current.reportValidity();
      if (showCompanyName) {
        company.ref.current?.reportValidity();
      }
      if (showCellPhone) {
        phone.ref.current?.reportValidity();
      }
      email.ref.current.reportValidity();
      if (showNote) {
        note.ref.current?.reportValidity();
      }
    }

    if (+amountType.value === paymentAmountType.other) {
      if (!(otherAmount.ref.current.checkValidity() && otherAmount.value > 0)) {
        formIsValid = false;
        otherAmount.ref.current.reportValidity();
      }
    } else {
      otherAmount.reset();
    }

    return formIsValid;
  };

  const reviewpayment = () => {
    const isValidForm = validateForm();
    if (isValidForm) {
      // Set button click for callbacks if the form is valid
      setbuttonClick({
        count: buttonClick.count + 1,
        scheduleFlag: false,
      });
    }
  };

  const getPaymentAmount = () => {
    let paymentAmount = accountInfo?.amountDue; //TODO: use configration like convenience fee and escrow amount.
    if (+amountType.value === paymentAmountType.due_amount) {
      paymentAmount = accountInfo?.amountDue;
    } else if (+amountType.value === paymentAmountType.other_due_amount) {
      paymentAmount = accountInfo?.otherAmountDue;
    } else if (+amountType.value === paymentAmountType.other) {
      paymentAmount = parseFloat(otherAmount.value) || 0;
    }
    return paymentAmount ?? 0;
  };

  const paymentHandler = async (currentMethod?: PaymentMethod) => {
    if (!validateForm()) {
      return;
    }
    setbuttonClick({
      count: 0,
      scheduleFlag: false,
    });

    if (
      currentMethod &&
      !currentMethod.ack &&
      currentMethod.paymentMethodType !== PAYMENT_METHOD_TYPE.eWallet
    ) {
      // Now we are allowing all payment method types to use this. At the time this is ACH and Card,
      // we will need to consider when ewallet is done.
      const authData: PaymentMethodAuthRequest =
        preparePaymentMethodAuthRequest(currentMethod, client.clientId);

      authData.accountType = accountInfo?.accountType;

      try {
        setIsLoading(true);

        const paymentMethodAuth = await request.post<PaymentMethodAuthResponse>(
          FT_PAYMENT_METHOD_AUTH,
          authData,
        );

        if (paymentMethodAuth.data.authReferenceId) {
          currentMethod.authReferenceId =
            paymentMethodAuth.data.authReferenceId;
        }
        if (paymentMethodAuth.data.cardBinType) {
          currentMethod.cardBinType = paymentMethodAuth.data.cardBinType;
        }
      } catch (e) {
        const errCode = extractError(e);
        addNotification({
          messages: errCode,
          type: "ERROR",
          closable: true,
        });

        setIsLoading(false);
        // Return early to cancel the flow
        return;
      }
    }

    const paymentAmount = getPaymentAmount();

    const convenienceFee = determineFeeConfiguration(
      currentMethod,
      currentPaymentMethod ? config.paymentMethods[currentPaymentMethod] : null,
      false,
      paymentCenter.defaultFees.convenienceFee!,
    );
    const feeTotal = calculateFee(
      convenienceFee,
      paymentCenter.defaultFees.convenienceFee,
      paymentAmount,
    );

    const dataToSend: PaymentData = {
      paymentDate: new Date().toUTCString(),
      paymentAmount: paymentAmount,
      feeTotal,
      paymentMethod: currentMethod ? currentMethod : {},
      phone: showCellPhone ? phone.value : null,
      company: showCompanyName ? company.value : null,
      note: showNote ? note.value : null,
      accountInformationId: accountInfo?.id,
      paymentType: paymentType.value,
      savedPaymentMethodId: currentMethod?.id ? currentMethod?.id! : undefined,
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value ? email.value.trim() : null,
      applicationName: "payment-center",
      clientId: client.clientId || 0,
      paymentAmountType: parseInt(amountType.value),
      customerReferenceNumber: paymentData?.customerReferenceNumber,
      walletType: currentMethod?.walletType || null,
      jsonPayload: currentMethod?.jsonPayload || undefined,
    };
    dispatch(setPaymentData(dataToSend));
    setIsLoading(false);
    navigate(`/${client.customerKey}/quick-review`);
  };

  const backButtonHandler = () => {
    dispatch(clearAllNotifications());
    navigate(`/${client.customerKey}/quick-info`);
  };

  const renderPaymentType = () => {
    const items: JSX.Element[] = [];
    config.paymentTypes.forEach((paymentTypeConfig) => {
      const item = (
        <ft-list-item
          key={paymentTypeConfig.code}
          {...renderConditionalProp(
            paymentType.value === paymentTypeConfig.code,
            "selected",
          )}
          value={paymentTypeConfig.code}
        >
          {paymentTypeConfig.name}
        </ft-list-item>
      );
      items.push(item);
    });
    return items;
  };

  return (
    <SidePageWrapper hideDisclaimeIcon={true} hideDisclaime={true}>
      <div className={classes.Container}>
        <QuickPayBar
          progress="0.5"
          quickPayType={paymentCenter.quickPayments.quickPayType}
          titles={{
            loan: t("quickpay:loan"),
            information: t("quickpay:information"),
            payment: t("quickpay:payment"),
            review: t("quickpay:review"),
            complete: t("quickpay:complete"),
          }}
          activeStep={QuickPayStep.Payment}
        />
        <Expension />
        <ft-divider class={classes.Divider}></ft-divider>
        {accountMethods.length !== 0 && (
          <>
            <div className={classes.BasicInfo}>
              <ft-textfield
                data-testid="first-name-quickpay-field"
                class={classes.BasicInfo_fields}
                outlined
                required
                ref={firstName.ref}
                label={t("account_next:first_name_label")}
                validationMessage={t("common:invalid_field_generic")}
                type={"text"}
                onInput={firstName.valueChangeHandler}
                value={firstName.value}
                onBlur={firstName.inputBlurHandler}
                maxLength={50}
                pattern={genericRegex}
              ></ft-textfield>
              <ft-textfield
                data-testid="last-name-quickpay-field"
                class={classes.BasicInfo_fields}
                outlined
                required
                ref={lastName.ref}
                label={t("account_next:last_name_label")}
                validationMessage={t("common:invalid_field_generic")}
                type={"text"}
                onInput={lastName.valueChangeHandler}
                value={lastName.value}
                onBlur={lastName.inputBlurHandler}
                maxLength={50}
                pattern={genericRegex}
              ></ft-textfield>
              {showCellPhone && (<ft-textfield
                class={classes.BasicInfo_fields}
                suffix={t("payment:optional")}
                outlined
                label={t("account_next:cell_phone")}
                validationMessage={t("common:invalid_field_generic")}
                type={"text"}
                autoFormat={true}
                onInput={phone.valueChangeHandler}
                value={phone.value}
                onBlur={phone.inputBlurHandler}
                ref={phone.ref}
                pattern={phoneRegex}
              ></ft-textfield>)}
              <ft-textfield
                class={classes.BasicInfo_fields}
                suffix={isNonValidated ? undefined : t("payment:optional")}
                pattern={optionEmailRegex}
                outlined
                label={t("account_next:email_label")}
                validationMessage={t("common:email_validation")}
                type={"email"}
                {...renderConditionalProp(isNonValidated, "required")}
                ref={email.ref}
                onInput={email.valueChangeHandler}
                value={email.value}
                onBlur={email.inputBlurHandler}
              ></ft-textfield>
              {showCompanyName && (<ft-textfield
                class={classes.BasicInfo_fields}
                suffix={t("payment:optional")}
                outlined
                label={t("account_next:company_name_label")}
                validationMessage={t("common:invalid_field_generic")}
                type={"text"}
                onInput={company.valueChangeHandler}
                value={company.value}
                onBlur={company.inputBlurHandler}
                ref={company.ref}
                maxLength={50}
                pattern={genericRegex}
              ></ft-textfield>)}
              {showNote && (<ft-textfield
                class={[
                  classes.BasicInfo_fields,
                  classes.BasicInfo_fullRow,
                ].join(" ")}
                suffix={t("payment:optional")}
                outlined
                label={t("account_next:note_label")}
                validationMessage={t("common:validation_character")}
                type={"text"}
                onInput={note.valueChangeHandler}
                value={note.value}
                onBlur={note.inputBlurHandler}
                ref={note.ref}
                maxLength={150}
                pattern={genericRegex}
              ></ft-textfield>)}
              {Object.keys(config.paymentTypes).length === 1 ? null : (
                <ft-select
                  validationMessage=" "
                  onClick={paymentType.valueChangeHandler}
                  value={paymentType.value}
                  onBlur={paymentType.inputBlurHandler}
                  outlined
                  label={t("payment_next:payment_type")}
                  id="payment-type"
                  class={classes.PaymentTypeSelect}
                >
                  {renderPaymentType()}
                </ft-select>
              )}
            </div>
            {currentPaymentConfig && (
              <div className={classes.PaymentAmount}>
                <p className={classes.PaymentAmount_heading}>
                  {t("payment_final:payment_amount")}
                </p>
                <div className={classes.PaymentAmount_container}>
                  {currentPaymentConfig?.payments?.amountSelectionOption.includes(
                    "amount-due",
                  ) &&
                  (amountDueInRange || otherAmountDueInRange) ? (
                    <div className={classes.LeftWrappper}>
                      <div className={classes.AmountDue}>
                        {amountDueInRange && (
                          <ft-formfield
                            label={`${t("common:amount_due")}: ${t(
                              "common:currency_format",
                              { value: accountInfo?.amountDue },
                            )}`}
                          >
                            <ft-radio
                              {...renderConditionalProp(
                                +amountType.value ===
                                  paymentAmountType.due_amount,
                                "checked",
                              )}
                              onClick={amountType.valueChangeHandler}
                              name="payment-amount"
                              data-testid="amount-due-radiobutton"
                              value={paymentAmountType.due_amount}
                            ></ft-radio>
                          </ft-formfield>
                        )}
                      </div>
                      {otherAmountDueInRange && (
                        <ft-formfield
                          label={`${t("common:other_amount_due")}: ${t(
                            "common:currency_format",
                            { value: accountInfo?.otherAmountDue },
                          )}`}
                        >
                          <ft-radio
                            {...renderConditionalProp(
                              +amountType.value ===
                                paymentAmountType.other_due_amount,
                              "checked",
                            )}
                            onClick={amountType.valueChangeHandler}
                            data-testid="other-amount-radio-button"
                            name="payment-amount"
                            value={paymentAmountType.other_due_amount}
                          ></ft-radio>
                        </ft-formfield>
                      )}
                    </div>
                  ) : null}

                  {currentPaymentConfig?.payments?.amountSelectionOption.includes(
                    "user-provided",
                  ) && (
                    <div
                      className={
                        currentPaymentConfig?.payments?.amountSelectionOption.includes(
                          "amount-due",
                        ) && amountDueInRange
                          ? classes.Other
                          : classes.OtherContainer
                      }
                    >
                      <ft-formfield
                        label={`${t("payment_next:other_amount")} $:`}
                      >
                        <ft-radio
                          {...renderConditionalProp(
                            +amountType.value === paymentAmountType.other,
                            "checked",
                          )}
                          onClick={amountType.valueChangeHandler}
                          name="payment-amount"
                          data-testid="other-amount-radiobutton"
                          value={paymentAmountType.other}
                        ></ft-radio>
                      </ft-formfield>
                      <ft-textfield
                        data-testid="other-amount-text-field"
                        class={classes.PaymentAmount_amount}
                        outlined
                        label={t("payment:amount")}
                        customInput="test"
                        type={"number"}
                        step={".01"}
                        min={
                          currentPaymentConfig?.payments
                            ?.minimumPaymentAmount || 0.1
                        }
                        max={
                          currentPaymentConfig?.payments?.maximumPaymentAmount
                        }
                        ref={otherAmount.ref}
                        validationMessage={t("payment_next:greater_than_zero", {
                          min: currentPaymentConfig?.payments
                            ?.minimumPaymentAmount,
                          max: currentPaymentConfig?.payments
                            ?.maximumPaymentAmount,
                        })}
                        onInput={otherAmount.decimalValueHandler}
                        value={otherAmount.value}
                        onBlur={otherAmount.inputBlurHandler}
                        {...renderConditionalProp(
                          +amountType.value === paymentAmountType.other,
                          "required",
                        )}
                        icon="attach_money"
                        {...renderConditionalProp(
                          +amountType.value !== paymentAmountType.other,
                          "disabled",
                        )}
                        onWheel={(e: WheelEvent) =>
                          e.target instanceof HTMLElement && e.target.blur()
                        }
                      ></ft-textfield>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={classes.SectionWrapper}>
              <div className={classes.Heading}>
                {t("payment_next:choose_payment_method")}
                <p className={classes.Convenience_amount}>
                  {t("payment_next:processing_fee")}
                </p>
              </div>
              <AddPaymentMethods
                buttonClick={buttonClick}
                updateButtonClick={updateButtonClickFromChild}
                paymentHandler={paymentHandler}
                paymentAmount={getPaymentAmount()}
                inlineView={true}
                paymentActionType={PaymentActionType.PAY}
                borderView={true}
              />
              <Disclaimer
                translatedText={
                  <Trans
                    i18nKey="quickpay:disclaimer_text"
                    values={{
                      convenienceFee: paymentCenter.defaultFees.convenienceFee,
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                }
              />
            </div>
          </>
        )}
        <div className={classes.Review_payment}>
          {/* Conditionally render the back button based on deepLinkingOnly */}
          {!paymentCenter.quickPayments?.deepLinkingOnly && (
            <ft-button
              onClick={backButtonHandler}
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:back_label")}
              outlined
            ></ft-button>
          )}
          {accountMethods.length !== 0 && (
            <ft-button
              data-testid="Review-payment-quickpay-first-button"
              class={classes.Make_payment}
              label={t("payment_next:review_payment")}
              unelevated
              onClick={reviewpayment}
            ></ft-button>
          )}
        </div>
      </div>
      {isLoading ? <ft-screen-loader></ft-screen-loader> : null}
    </SidePageWrapper>
  );
};
